import { useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'

import { FormEvent, useState } from 'react'

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton } from '@/components/ui'
import { NeedsConnection } from '@/components/pos/office/NeedsConnection'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { useSession } from '@/contexts/SessionContext'
import { useOfficeQuery, useOnline } from '@/hooks/features/office'
import { OfficeModel } from '@/http/models'

type Row = Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any

const FIELDS = [
  ['name', 'Name'],
  ['contact_person', 'Contact person'],
  ['contact_number', 'Phone'],
  ['email', 'Email'],
  ['address', 'Address'],
] as const

/** Suppliers are business-wide and online (`/supplier`). */
export const Suppliers = () => {
  const { can } = useSession()
  const online = useOnline()
  const client = useQueryClient()
  const { data, isFetching } = useOfficeQuery(['suppliers'], () => OfficeModel.suppliers())
  const [editing, setEditing] = useState<Row | 'new' | null>(null)
  if (!online) return <NeedsConnection />

  const onSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    const body = Object.fromEntries(FIELDS.map(([k]) => [k, String(f.get(k) ?? '').trim() || undefined]))
    const res = editing === 'new' ? await OfficeModel.createSupplier(body) : await OfficeModel.updateSupplier(String(editing!._id), body)
    if (!res.success) return toast.error(res.error?.message ?? 'Could not save the supplier.')
    toast.success(`${body.name} saved`)
    setEditing(null)
    client.invalidateQueries({ queryKey: ['office'] })
  }

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-auto px-4 pb-4 md:px-6">
      {can('suppliers:create') && (
        <PosButton onClick={() => setEditing('new')}>
          <Plus /> New supplier
        </PosButton>
      )}
      <OfficeTable
        cards
        loading={isFetching}
        rows={data?.result ?? []}
        rowKey={(r) => String(r._id)}
        empty="No suppliers yet."
        onRowClick={can('suppliers:update') ? (r) => setEditing(r) : undefined}
        columns={[
          { label: 'Name', cell: (r) => <span className="font-medium text-ink">{r.name}</span> },
          { label: 'Contact', className: 'hidden md:table-cell', cell: (r) => r.contact_person ?? '' },
          { label: 'Phone', cell: (r) => r.contact_number ?? '' },
          { label: 'Email', className: 'hidden lg:table-cell', cell: (r) => r.email ?? '' },
        ]}
      />
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="sm">
          <form key={editing === 'new' ? 'new' : String(editing?._id)} onSubmit={onSave} className="space-y-3">
            <DialogHeader>
              <DialogTitle>{editing === 'new' ? 'New supplier' : 'Edit supplier'}</DialogTitle>
            </DialogHeader>
            {FIELDS.map(([key, label]) => (
              <div key={key} className="space-y-1.5">
                <Label htmlFor={`s-${key}`}>{label}</Label>
                <Input
                  id={`s-${key}`}
                  name={key}
                  required={key === 'name'}
                  type={key === 'email' ? 'email' : 'text'}
                  defaultValue={editing && editing !== 'new' ? (editing[key] ?? '') : ''}
                  className="h-11"
                />
              </div>
            ))}
            <DialogFooter>
              <PosButton type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </PosButton>
              <PosButton type="submit">Save</PosButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
